
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Email Service Module
 * Provides email functionality using Google Workspace Gmail API
 * Supports both mock mode (development) and real API mode (production)
 */

import { BookingDetails, VoucherDetails, ContactDetails } from '../types';
import { APP_CONFIG, API_KEYS } from '../config';
import { Logger } from './logger';
import { getGWorkspaceAccessToken, getGWorkspaceAuthUrl, exchangeCodeForTokens } from './tokenService';
import { apiClient, isExternalBackend } from './apiClient';
export { getGWorkspaceAccessToken, getGWorkspaceAuthUrl, exchangeCodeForTokens };

/**
 * Validates email parameters before sending.
 * Throws an error if parameters are invalid.
 */
export const validateEmailParams = (to: string, subject: string, htmlBody: string, textBody: string): void => {
    if (!to || !to.includes('@')) {
        throw new Error(`Invalid recipient email: ${to}`);
    }
    if (!subject || subject.trim().length === 0) {
        throw new Error('Email subject is required');
    }
    if ((!htmlBody || htmlBody.trim().length === 0) && (!textBody || textBody.trim().length === 0)) {
        throw new Error('Email body (HTML or text) is required');
    }
};

// Email template builders
export const buildBookingConfirmationEmail = (details: BookingDetails, meetLink: string, eventId: string): { subject: string; html: string; text: string } => {
    const subject = `Booking Confirmation - ${details.name}`;

    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
        .info-row { margin: 15px 0; padding: 10px; background: white; border-left: 4px solid #667eea; }
        .label { font-weight: bold; color: #667eea; }
        .button { display: inline-block; padding: 12px 30px; background: #667eea; color: white; text-decoration: none; border-radius: 5px; margin: 20px 0; }
        .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>✨ Booking Confirmed!</h1>
        </div>
        <div class="content">
            <p>Hello Konstantina,</p>
            <p>A new styling session has been booked:</p>
            
            <div class="info-row">
                <span class="label">Client Name:</span> ${details.name}
            </div>
            <div class="info-row">
                <span class="label">Email:</span> ${details.email}
            </div>
            ${details.phone ? `<div class="info-row"><span class="label">Phone:</span> ${details.phone}</div>` : ''}
            <div class="info-row">
                <span class="label">Date:</span> ${details.date}
            </div>
            <div class="info-row">
                <span class="label">Time:</span> ${details.time}
            </div>
            <div class="info-row">
                <span class="label">Services:</span> ${details.serviceIds?.join(', ') || 'N/A'}
            </div>
            ${details.notes ? `<div class="info-row"><span class="label">Notes:</span> ${details.notes}</div>` : ''}
            
            <div style="margin: 30px 0; padding: 20px; background: #e8f4f8; border-radius: 5px;">
                <p style="margin: 0 0 10px 0;"><strong>Google Meet Link:</strong></p>
                <a href="${meetLink}" class="button" style="color: white;">Join Meeting</a>
                <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">Link: ${meetLink}</p>
            </div>
            
            <div style="margin-top: 20px; padding: 15px; background: white; border-radius: 5px;">
                <p style="margin: 0;"><strong>Calendar Event ID:</strong> ${eventId}</p>
            </div>
        </div>
        <div class="footer">
            <p>This is an automated notification from Constantina Lifestyle</p>
            <p>${APP_CONFIG.DOMAIN}</p>
        </div>
    </div>
</body>
</html>
    `;

    const text = `
Booking Confirmation - ${details.name}

Hello Konstantina,

A new styling session has been booked:

Client Name: ${details.name}
Email: ${details.email}
${details.phone ? `Phone: ${details.phone}` : ''}
Date: ${details.date}
Time: ${details.time}
Services: ${details.serviceIds.join(', ')}
${details.notes ? `Notes: ${details.notes}` : ''}

Google Meet Link: ${meetLink}
Calendar Event ID: ${eventId}

---
This is an automated notification from Constantina Lifestyle
${APP_CONFIG.DOMAIN}
    `;

    return { subject, html, text };
};

export const buildVoucherPurchaseEmail = (details: VoucherDetails, voucherCode: string): { subject: string; html: string; text: string } => {
    const subject = `New Voucher Purchase - ${voucherCode}`;

    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
        .info-row { margin: 15px 0; padding: 10px; background: white; border-left: 4px solid #f5576c; }
        .label { font-weight: bold; color: #f5576c; }
        .voucher-code { font-size: 24px; font-weight: bold; color: #f5576c; text-align: center; padding: 20px; background: white; border-radius: 5px; margin: 20px 0; }
        .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🎁 New Voucher Purchase!</h1>
        </div>
        <div class="content">
            <p>Hello Konstantina,</p>
            <p>A new gift voucher has been purchased:</p>
            
            <div class="voucher-code">
                Voucher Code: ${voucherCode}
            </div>
            
            <div class="info-row">
                <span class="label">Recipient Name:</span> ${details.recipientName}
            </div>
            <div class="info-row">
                <span class="label">Recipient Email:</span> ${details.recipientEmail}
            </div>
            <div class="info-row">
                <span class="label">Services:</span> ${details.serviceIds?.join(', ') || 'N/A'}
            </div>
            <div class="info-row">
                <span class="label">Personal Message:</span><br>
                ${details.message}
            </div>
        </div>
        <div class="footer">
            <p>This is an automated notification from Constantina Lifestyle</p>
            <p>${APP_CONFIG.DOMAIN}</p>
        </div>
    </div>
</body>
</html>
    `;

    const text = `
New Voucher Purchase - ${voucherCode}

Hello Konstantina,

A new gift voucher has been purchased:

Voucher Code: ${voucherCode}

Recipient Name: ${details.recipientName}
Recipient Email: ${details.recipientEmail}
Services: ${details.serviceIds?.join(', ') || 'N/A'}
Personal Message: ${details.message}

---
This is an automated notification from Constantina Lifestyle
${APP_CONFIG.DOMAIN}
    `;

    return { subject, html, text };
};

export const buildContactFormEmail = (details: ContactDetails): { subject: string; html: string; text: string } => {
    const subject = `Contact Form: ${details.subject || 'New Inquiry'}`;

    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
        .info-row { margin: 15px 0; padding: 10px; background: white; border-left: 4px solid #4facfe; }
        .label { font-weight: bold; color: #4facfe; }
        .message-box { padding: 20px; background: white; border-radius: 5px; margin: 20px 0; white-space: pre-wrap; }
        .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>📧 New Contact Form Submission</h1>
        </div>
        <div class="content">
            <p>Hello Konstantina,</p>
            <p>You have received a new message from your website:</p>
            
            <div class="info-row">
                <span class="label">Name:</span> ${details.name}
            </div>
            <div class="info-row">
                <span class="label">Email:</span> ${details.email}
            </div>
            ${details.phone ? `<div class="info-row"><span class="label">Phone:</span> ${details.phone}</div>` : ''}
            ${details.subject ? `<div class="info-row"><span class="label">Subject:</span> ${details.subject}</div>` : ''}
            
            <div style="margin: 20px 0;">
                <p style="margin: 0 0 10px 0; font-weight: bold; color: #4facfe;">Message:</p>
                <div class="message-box">${details.message}</div>
            </div>
            
            <div style="margin-top: 20px; padding: 15px; background: #fff9e6; border-left: 4px solid #ffc107; border-radius: 5px;">
                <p style="margin: 0;"><strong>💡 Reply to:</strong> <a href="mailto:${details.email}">${details.email}</a></p>
            </div>
        </div>
        <div class="footer">
            <p>This is an automated notification from Constantina Lifestyle</p>
            <p>${APP_CONFIG.DOMAIN}</p>
        </div>
    </div>
</body>
</html>
    `;

    const text = `
New Contact Form Submission

Hello Konstantina,

You have received a new message from your website:

Name: ${details.name}
Email: ${details.email}
${details.phone ? `Phone: ${details.phone}` : ''}
${details.subject ? `Subject: ${details.subject}` : ''}

Message:
${details.message}

Reply to: ${details.email}

---
This is an automated notification from Constantina Lifestyle
${APP_CONFIG.DOMAIN}
    `;

    return { subject, html, text };
};

export const buildMembershipPaymentEmail = (
    userName: string,
    userEmail: string,
    tierName: string,
    amount: string,
    transactionId: string
): { subject: string; html: string; text: string } => {
    const subject = `Membership Confirmation - ${tierName}`;

    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
        .info-row { margin: 15px 0; padding: 10px; background: white; border-left: 4px solid #667eea; }
        .label { font-weight: bold; color: #667eea; }
        .success-badge { display: inline-block; padding: 5px 15px; background: #edfaf0; color: #2e7d32; border-radius: 20px; font-weight: bold; margin-bottom: 20px; }
        .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>✨ Welcome to ${tierName}!</h1>
        </div>
        <div class="content">
            <div class="success-badge">Payment Successful</div>
            <p>Hello ${userName || 'there'},</p>
            <p>Thank you for your membership upgrade. Your payment has been processed successfully, and your account has been updated.</p>
            
            <div class="info-row">
                <span class="label">Plan:</span> ${tierName}
            </div>
            <div class="info-row">
                <span class="label">Amount Paid:</span> ${amount}
            </div>
            <div class="info-row">
                <span class="label">Transaction ID:</span> ${transactionId}
            </div>
            <div class="info-row">
                <span class="label">User Email:</span> ${userEmail}
            </div>

            <p>You now have access to all the features included in the ${tierName} plan. Happy styling!</p>
        </div>
        <div class="footer">
            <p>This is an automated notification from Constantina Lifestyle</p>
            <p>${APP_CONFIG.DOMAIN}</p>
        </div>
    </div>
</body>
</html>
    `;

    const text = `
Membership Confirmation - ${tierName}

Hello ${userName || 'there'},

Thank you for your membership upgrade. Your payment has been processed successfully, and your account has been updated.

Plan: ${tierName}
Amount Paid: ${amount}
User Email: ${userEmail}
Transaction ID: ${transactionId}

You now have access to all the features included in the ${tierName} plan. Happy styling!

---
This is an automated notification from Constantina Lifestyle
${APP_CONFIG.DOMAIN}
    `;

    return { subject, html, text };
};

export const buildActivationEmail = (email: string, activationLink: string): { subject: string; html: string; text: string } => {
    const subject = 'Activate your Constantina Account';
    const html = `
<!DOCTYPE html>
<html>
<body>
    <h1>Welcome to Constantina!</h1>
    <p>Please click the link below to activate your account:</p>
    <a href="${activationLink}">Activate Account</a>
    <p>Or copy this link: ${activationLink}</p>
</body>
</html>
    `;
    const text = `Welcome! Please activate your account: ${activationLink}`;
    return { subject, html, text };
};

export const buildAdminNotificationEmail = (userEmail: string, date: string): { subject: string; html: string; text: string } => {
    const subject = 'New User Registration';
    const html = `
<!DOCTYPE html>
<html>
<body>
    <h1>New User Registered</h1>
    <p>Email: ${userEmail}</p>
    <p>Date: ${date}</p>
</body>
</html>
    `;
    const text = `New User Registered. Email: ${userEmail}, Date: ${date}`;
    return { subject, html, text };
};

/**
 * Send email via Google Workspace Gmail API
 * This is a mock implementation that simulates the API call.
 * For production, replace with actual Gmail API integration.
 */
export const sendEmail = async (
    to: string,
    subject: string,
    htmlBody: string,
    textBody: string,
    from: string = APP_CONFIG.EMAIL_FROM,
    fromName: string = APP_CONFIG.EMAIL_FROM_NAME
): Promise<void> => {
    // Validate parameters first
    validateEmailParams(to, subject, htmlBody, textBody);

    // When external backend is enabled, route email through backend API
    if (isExternalBackend()) {
        try {
            await apiClient.post('/api/email/send', { to, subject, text: textBody, html: htmlBody });
            Logger.info(`Email sent via backend API`, { to, subject });
            return;
        } catch (error) {
            Logger.error('Failed to send email via backend API, falling back to client-side', error);
            // Fall through to client-side sending
        }
    }

    // Check if we have API credentials configured FIRST to avoid unnecessary token fetch
    const missingKeys: string[] = [];
    if (!API_KEYS.emailjs.serviceId || API_KEYS.emailjs.serviceId.includes('placeholder')) {
        missingKeys.push('API_KEYS.emailjs.serviceId');
    }
    if (!API_KEYS.emailjs.publicKey || API_KEYS.emailjs.publicKey.includes('placeholder')) {
        missingKeys.push('API_KEYS.emailjs.publicKey');
    }
    if (!API_KEYS.googleWorkspace.refreshToken || API_KEYS.googleWorkspace.refreshToken.includes('PLACEHOLDER')) {
        missingKeys.push('API_KEYS.googleWorkspace.refreshToken');
    }

    const hasApiKey = missingKeys.length < 3; // True if at least one provider has keys

    if (!hasApiKey) {
        // Mock mode - just log the email
        Logger.info(`[MOCK MODE] Email would be sent. Missing config keys: ${missingKeys.join(', ')}`, {
            from: `${fromName} <${from}>`,
            to,
            subject,
            bodyPreview: textBody.substring(0, 100) + '...'
        });

        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 400));
        return;
    }


    /**
     * Converts a UTF-8 string to base64 (safe for all Unicode characters)
     */
    function utf8ToBase64(str: string): string {
        // Use TextEncoder to convert UTF-8 string to Uint8Array
        const encoder = new TextEncoder();
        const bytes = encoder.encode(str);

        // Convert bytes to binary string
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        // Use btoa on the binary string (which is now Latin1-safe)
        return btoa(binary);
    }

    // Determine active provider allowing UI override via localStorage
    let activeProvider = APP_CONFIG.EMAIL_PROVIDER;
    if (typeof window !== 'undefined') {
        activeProvider = localStorage.getItem('codina_email_provider') || APP_CONFIG.EMAIL_PROVIDER;
    }

    // Check if EmailJS is the selected provider or if we have keys and want to use it
    if (activeProvider === 'emailjs' || (API_KEYS.emailjs.serviceId !== 'service_placeholder' && !hasApiKey)) {
        try {
            Logger.info(`Sending email via EmailJS...`);

            const emailJsPayload = {
                service_id: API_KEYS.emailjs.serviceId,
                template_id: API_KEYS.emailjs.templateId,
                user_id: API_KEYS.emailjs.publicKey,
                accessToken: API_KEYS.emailjs.accessToken,
                template_params: {
                    to_email: to,
                    to_name: to.split('@')[0],
                    email: to,
                    recipient_email: to,
                    user_email: to,
                    reply_to: from,
                    from_email: from,
                    from_name: fromName,
                    subject: subject,
                    message_html: htmlBody,
                    message_text: textBody
                }
            };

            const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(emailJsPayload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`EmailJS API error: ${errorText || response.statusText}`);
            }

            Logger.info(`Email sent successfully via EmailJS`);
            return;
        } catch (error) {
            Logger.error('Failed to send email via EmailJS', error);
            // Fallback to mock logging if EmailJS fails, or throw if critical
            Logger.info(`[FALLBACK MOCK] Email would be sent:`, {
                from: `${fromName} <${from}>`,
                to,
                subject,
                bodyPreview: textBody.substring(0, 100) + '...'
            });
            return;
        }
    }

    // Only fetch token if we have valid credentials
    const accessToken = await getGWorkspaceAccessToken();

    // Real API mode (to be implemented when credentials are available)
    /* --- REAL GMAIL API IMPLEMENTATION --- ----------------------------------- */
    try {
        // Create the email in RFC 2822 format
        const email = [
            `From: ${fromName} <${from}>`,
            `To: ${to}`,
            `Subject: ${subject}`,
            'MIME-Version: 1.0',
            'Content-Type: multipart/alternative; boundary="boundary-string"',
            '',
            '--boundary-string',
            'Content-Type: text/plain; charset="UTF-8"',
            '',
            textBody,
            '',
            '--boundary-string',
            'Content-Type: text/html; charset="UTF-8"',
            '',
            htmlBody,
            '',
            '--boundary-string--'
        ].join('\r\n');

        // Encode the email in base64url format (UTF-8 safe)
        const encodedEmail = utf8ToBase64(email)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');

        // Send via Gmail API

        const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken} `,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                raw: encodedEmail
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Gmail API error: ${error.error?.message || response.statusText} `);
        }

        const result = await response.json();
        Logger.info(`Email sent successfully via Gmail API`, { messageId: result.id });
    } catch (error) {
        Logger.error('Failed to send email via Gmail API', error);
        throw error;
    }
};

export const emailService = {
    sendEmail,
    validateEmailParams,
    buildBookingConfirmationEmail,
    buildVoucherPurchaseEmail,
    buildContactFormEmail,
    buildMembershipPaymentEmail,
    buildActivationEmail,
    buildAdminNotificationEmail
};
